import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import L from 'leaflet';
import { toast } from 'sonner';
import { History, MapPin, RefreshCw, Search, Settings, Smartphone, Copy, FileText } from 'lucide-react';
import HRHeader, { getHRUser } from '@/components/HRHeader';
import {
  STATUS, authHeaders, coords, escapeHtml, formatTime, minutesBetween, formatMinutes, siteAt, timeAgo, todayIST, useLeafletMap,
} from '@/lib/tracking';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REFRESH_MS = 30000;

/** "2 min ago" while live; for a silent phone say plainly that the position is old */
function lastUpdateText(emp, now) {
  if (emp.status === 'tracking_interrupted') {
    return `${emp.problem || 'Tracking interrupted'}${emp.last_point_at ? ` — last location ${timeAgo(emp.last_point_at, now)}` : ''}`;
  }
  if (!emp.last_point_at) return '—';
  if (emp.status === 'stale') return `Offline — last location received ${formatMinutes(minutesBetween(emp.last_point_at, now))} ago`;
  return timeAgo(emp.last_point_at, now);
}

export default function HRTracking() {
  const navigate = useNavigate();
  const [user] = useState(getHRUser);
  const [data, setData] = useState(null);
  const [locations, setLocations] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [search, setSearch] = useState('');
  const [showOffDuty, setShowOffDuty] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [mapRef, map] = useLeafletMap();
  const layerRef = useRef(null);
  const markersRef = useRef({});
  const fittedRef = useRef(false);

  useEffect(() => {
    if (!user) navigate('/hr/login');
  }, [user, navigate]);

  const load = useCallback(async () => {
    try {
      const [live, locs] = await Promise.all([
        axios.get(`${API}/tracking/live`, { headers: authHeaders() }),
        axios.get(`${API}/locations`),
      ]);
      setData(live.data);
      setLocations(locs.data);
      setLoadError(null);
      setNow(Date.now());
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        navigate('/hr/login');
        return;
      }
      setLoadError(error.response ? 'Server error while loading tracking data.' : 'Cannot reach the server. Retrying…');
    }
  }, [navigate]);

  useEffect(() => {
    if (!user) return undefined;
    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => clearInterval(timer);
  }, [user, load]);

  const employees = useMemo(() => {
    const term = search.trim().toLowerCase();
    const order = { tracking_interrupted: 0, on_duty: 1, stale: 2, off_duty: 3 };
    return (data?.employees || [])
      .filter((e) => showOffDuty || e.status !== 'off_duty')
      .filter((e) => !term || [e.name, e.employee_id, e.department].some((v) => v?.toLowerCase().includes(term)))
      .sort((a, b) => order[a.status] - order[b.status] || a.name.localeCompare(b.name));
  }, [data, search, showOffDuty]);

  const counts = useMemo(() => {
    const c = { on_duty: 0, tracking_interrupted: 0, stale: 0, off_duty: 0 };
    (data?.employees || []).forEach((e) => { c[e.status] += 1; });
    return c;
  }, [data]);

  // Markers: one per employee with a known position today
  useEffect(() => {
    if (!map) return;
    if (!layerRef.current) layerRef.current = L.layerGroup().addTo(map);
    layerRef.current.clearLayers();
    markersRef.current = {};
    const today = todayIST();
    locations.forEach((loc) => {
      L.circle([loc.latitude, loc.longitude], { radius: loc.radius || 100, color: '#52525b', weight: 1, fillOpacity: 0.05 })
        .bindTooltip(escapeHtml(loc.name))
        .addTo(layerRef.current);
    });
    const withPosition = employees.filter((e) => e.latitude != null);
    withPosition.forEach((emp) => {
      const status = STATUS[emp.status];
      const marker = L.circleMarker([emp.latitude, emp.longitude], {
        radius: 9, color: '#fff', weight: 2, fillColor: status.color, fillOpacity: 1,
      }).bindTooltip(escapeHtml(emp.name), { permanent: true, direction: 'top', offset: [0, -8] })
        .bindPopup(`
          <div style="min-width:200px">
            <div style="font-weight:700;font-size:14px">${escapeHtml(emp.name)}</div>
            <div style="color:#71717a">ID: ${escapeHtml(emp.employee_id)}${emp.department ? ` · ${escapeHtml(emp.department)}` : ''}</div>
            <div style="margin-top:6px"><b>${status.label}</b></div>
            <div>Last update: ${escapeHtml(lastUpdateText(emp, now))} (${formatTime(emp.last_point_at)})</div>
            ${emp.accuracy != null ? `<div>Accuracy: ±${Math.round(emp.accuracy)} m</div>` : ''}
            <a href="/hr/tracking/history?employee=${encodeURIComponent(emp.id)}&date=${today}" style="display:inline-block;margin-top:8px;font-weight:700">Open history →</a>
          </div>`)
        .on('click', () => setSelectedId(emp.id))
        .addTo(layerRef.current);
      markersRef.current[emp.id] = marker;
    });
    if (!fittedRef.current && withPosition.length) {
      map.fitBounds(L.latLngBounds(withPosition.map((e) => [e.latitude, e.longitude])).pad(0.3), { maxZoom: 15 });
      fittedRef.current = true;
    }
  }, [map, employees, locations, now]);

  const selected = (data?.employees || []).find((e) => e.id === selectedId);

  const focus = (emp) => {
    setSelectedId(emp.id);
    const marker = markersRef.current[emp.id];
    if (map && marker) {
      map.setView(marker.getLatLng(), Math.max(map.getZoom(), 15));
      marker.openPopup();
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-zinc-50">
      <HRHeader title="Employee Tracking" user={user} active="/hr/tracking" />
      <div className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        <div className="flex flex-wrap items-center gap-3">
          {['on_duty', 'tracking_interrupted', 'stale', 'off_duty'].map((key) => (
            <div key={key} className="bg-white border-2 border-zinc-100 rounded-lg px-4 py-3 flex items-center gap-3" data-testid={`count-${key}`}>
              <span className={`w-3 h-3 rounded-full ${STATUS[key].dot}`} />
              <span className="text-sm font-bold uppercase tracking-wider text-zinc-600">{STATUS[key].label}</span>
              <span className="text-2xl font-extrabold text-zinc-900">{counts[key]}</span>
            </div>
          ))}
          <button onClick={() => navigate('/hr/tracking/reports')} className="h-10 px-4 bg-zinc-900 text-white text-sm font-bold uppercase tracking-wider rounded-sm flex items-center gap-2" data-testid="open-tracking-reports">
            <FileText size={16} /> Reports
          </button>
          <button onClick={load} className="ml-auto flex items-center gap-2 text-sm font-medium text-zinc-600 hover:text-zinc-900" data-testid="refresh-tracking">
            <RefreshCw size={16} /> Updated {data ? formatTime(data.server_time) : '…'} · refreshes every 30 s
          </button>
        </div>
        {loadError && <div className="bg-red-50 border-2 border-red-200 text-red-800 rounded-lg p-3 text-sm font-medium">{loadError}</div>}
        <p className="text-xs text-zinc-500">
          Positions are the latest location each phone has uploaded. A phone without internet keeps recording and uploads later,
          so until then it shows as <b>Offline / stale</b> with the age of its last received location. <b>Tracking interrupted</b> means
          the phone is online but reports that it cannot record locations (GPS off, permission removed, or tracking stopped).
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white border-2 border-zinc-100 rounded-lg overflow-hidden">
            <div ref={mapRef} style={{ height: 480 }} data-testid="tracking-map" />
          </div>
          <div className="bg-white border-2 border-zinc-100 rounded-lg p-5" data-testid="tracking-details">
            {selected ? (
              <EmployeeDetails emp={selected} now={now} locations={locations} />
            ) : (
              <div className="text-center text-zinc-500 py-16">
                <MapPin size={40} className="mx-auto text-zinc-300 mb-3" />
                Click an employee on the map or in the list.
              </div>
            )}
          </div>
        </div>

        <div className="bg-white border-2 border-zinc-100 rounded-lg overflow-hidden">
          <div className="flex flex-wrap items-center gap-4 p-4 border-b-2 border-zinc-100">
            <div className="relative flex-1 min-w-[200px]">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, ID or department"
                className="w-full h-10 pl-9 pr-3 border-2 border-zinc-200 rounded-sm text-sm focus:border-zinc-900 focus:outline-none"
                data-testid="tracking-search"
              />
            </div>
            <label className="flex items-center gap-2 text-sm font-medium text-zinc-700">
              <input type="checkbox" checked={showOffDuty} onChange={(e) => setShowOffDuty(e.target.checked)} />
              Show off-duty employees
            </label>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="tracking-table">
              <thead className="bg-zinc-50 border-b-2 border-zinc-100">
                <tr>
                  {['Employee ID', 'Name', 'Department', 'Type', 'Duty Status', 'Last Location', 'Last Update', 'Accuracy', ''].map((h) => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-bold text-zinc-600 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {employees.map((emp) => {
                  const site = emp.latitude != null ? siteAt(emp.latitude, emp.longitude, locations) : null;
                  return (
                    <tr key={emp.id} className={`border-b border-zinc-100 hover:bg-zinc-50 ${emp.id === selectedId ? 'bg-zinc-50' : ''}`} data-testid={`tracking-row-${emp.employee_id}`}>
                      <td className="px-4 py-3 font-mono text-sm">{emp.employee_id}</td>
                      <td className="px-4 py-3 font-bold text-zinc-900">{emp.name}</td>
                      <td className="px-4 py-3 text-sm text-zinc-600">{emp.department || '—'}</td>
                      <td className="px-4 py-3 text-xs text-zinc-600">{emp.field_tracking ? 'Field' : 'Not field'}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-2 px-2 py-1 rounded text-xs font-bold uppercase ${STATUS[emp.status].badge}`}>
                          <span className={`w-2 h-2 rounded-full ${STATUS[emp.status].dot}`} />{STATUS[emp.status].label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {emp.latitude == null ? '—' : site ? <span className="font-medium">At {site.name}</span> : (
                          <a className="font-mono text-xs underline text-zinc-700" target="_blank" rel="noreferrer"
                            href={`https://www.openstreetmap.org/?mlat=${emp.latitude}&mlon=${emp.longitude}#map=17/${emp.latitude}/${emp.longitude}`}>
                            {coords(emp.latitude, emp.longitude)}
                          </a>
                        )}
                      </td>
                      <td className={`px-4 py-3 text-sm ${emp.status === 'stale' ? 'text-amber-700 font-medium' : emp.status === 'tracking_interrupted' ? 'text-red-700 font-medium' : 'text-zinc-700'}`}>{lastUpdateText(emp, now)}</td>
                      <td className="px-4 py-3 text-sm text-zinc-600">{emp.accuracy != null ? `±${Math.round(emp.accuracy)} m` : '—'}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {emp.latitude != null && (
                          <button onClick={() => focus(emp)} className="text-xs font-bold uppercase text-zinc-700 hover:text-zinc-900 mr-3">Map</button>
                        )}
                        <button
                          onClick={() => navigate(`/hr/tracking/history?employee=${emp.id}&date=${todayIST()}`)}
                          className="text-xs font-bold uppercase text-zinc-700 hover:text-zinc-900"
                          data-testid={`history-${emp.employee_id}`}
                        >History</button>
                      </td>
                    </tr>
                  );
                })}
                {data && employees.length === 0 && (
                  <tr><td colSpan={9} className="text-center py-10 text-zinc-500">{(data.employees || []).length ? 'No employees match.' : 'No field employees yet. Tick "Field employee" for an employee on the Employees page.'}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <TrackingSettingsPanel />
        <AppReleasePanel />
      </div>
    </div>
  );
}

function EmployeeDetails({ emp, now, locations }) {
  const navigate = useNavigate();
  const session = emp.session;
  const site = emp.latitude != null ? siteAt(emp.latitude, emp.longitude, locations) : null;
  return (
    <div className="space-y-3">
      <div>
        <h2 className="font-heading text-xl font-bold uppercase text-zinc-900">{emp.name}</h2>
        <p className="font-mono text-sm text-zinc-500">ID: {emp.employee_id}{emp.department ? ` · ${emp.department}` : ''}</p>
      </div>
      <span className={`inline-flex items-center gap-2 px-2 py-1 rounded text-xs font-bold uppercase ${STATUS[emp.status].badge}`}>
        <span className={`w-2 h-2 rounded-full ${STATUS[emp.status].dot}`} />{STATUS[emp.status].label}
      </span>
      <dl className="text-sm space-y-2">
        <Row label="Start Duty" value={session ? formatTime(session.started_at) : 'Not started today'} />
        <Row label="End Duty" value={session?.ended_at ? formatTime(session.ended_at) : session ? 'Still on duty' : '—'} />
        <Row label="Latest location" value={emp.latitude == null ? '—' : site ? `At ${site.name}` : coords(emp.latitude, emp.longitude)} />
        <Row label="Last update" value={lastUpdateText(emp, now)} />
        <Row label="Accuracy" value={emp.accuracy != null ? `±${Math.round(emp.accuracy)} m` : '—'} />
        {emp.problem && <Row label="Problem" value={emp.problem} />}
        {emp.pending_points > 0 && <Row label="Waiting on phone" value={`${emp.pending_points} locations`} />}
        {emp.battery_percent != null && <Row label="Phone battery" value={`${emp.battery_percent}%`} />}
        <div>
          <dt className="text-zinc-500 mb-1">Assigned sites</dt>
          <dd className="text-zinc-900">
            {emp.assigned_location_ids.length === 0 ? 'All approved sites (none assigned)' : (
              <ul className="list-disc pl-5">
                {emp.assigned_location_ids.map((id) => <li key={id}>{locations.find((l) => l.id === id)?.name || 'Removed site'}</li>)}
              </ul>
            )}
          </dd>
        </div>
        {session?.start_source === 'app_offline' && <p className="text-xs text-amber-700">Duty was started while the phone was offline.</p>}
      </dl>
      <button
        onClick={() => navigate(`/hr/tracking/history?employee=${emp.id}&date=${todayIST()}`)}
        className="w-full h-11 bg-zinc-900 text-white font-bold uppercase tracking-wider rounded-sm flex items-center justify-center gap-2"
      >
        <History size={16} /> Location History
      </button>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-4">
    <dt className="text-zinc-500">{label}</dt>
    <dd className="font-medium text-zinc-900 text-right">{value}</dd>
  </div>
);

const SETTING_FIELDS = [
  ['interval_seconds', 'Record every (seconds)', 30, 1800],
  ['min_distance_m', 'Or after moving (metres)', 10, 1000],
  ['stale_after_minutes', 'Mark offline after (minutes)', 2, 240],
  ['max_accuracy_m', 'Ignore fixes worse than (metres)', 20, 2000],
  ['max_duty_hours', 'End duty automatically after (hours)', 1, 24],
  ['retention_days', 'Keep location history (days)', 7, 3650],
  ['visit_min_dwell_minutes', 'Visit: minimum stay (minutes)', 1, 120],
  ['visit_min_points', 'Visit: minimum locations inside', 1, 20],
  ['visit_exit_grace_minutes', 'Visit: ignore brief exits up to (minutes)', 0, 60],
  ['visit_radius_buffer_m', 'Visit: extra GPS tolerance (metres)', 0, 200],
  ['stop_radius_m', 'Other stops: radius (metres)', 30, 1000],
  ['stop_min_minutes', 'Other stops: minimum stay (minutes)', 2, 240],
];

function TrackingSettingsPanel() {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || settings) return;
    axios.get(`${API}/tracking/settings`, { headers: authHeaders() })
      .then((r) => setSettings(r.data))
      .catch(() => toast.error('Could not load tracking settings'));
  }, [open, settings]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await axios.put(`${API}/tracking/settings`, settings, { headers: authHeaders() });
      setSettings(r.data);
      toast.success('Tracking settings saved. Phones pick them up at their next duty start.');
    } catch (error) {
      toast.error(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'Invalid settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border-2 border-zinc-100 rounded-lg">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-2 p-4 font-bold uppercase tracking-wider text-sm text-zinc-900" data-testid="toggle-tracking-settings">
        <Settings size={16} /> Tracking settings
      </button>
      {open && settings && (
        <form onSubmit={save} className="p-4 pt-0 grid grid-cols-1 md:grid-cols-3 gap-4">
          {SETTING_FIELDS.map(([key, label, min, max]) => (
            <label key={key} className="text-sm">
              <span className="block text-zinc-500 font-medium mb-1">{label}</span>
              <input
                type="number" min={min} max={max} required value={settings[key]}
                onChange={(e) => setSettings({ ...settings, [key]: Number(e.target.value) })}
                className="w-full h-10 px-3 border-2 border-zinc-200 rounded-sm focus:border-zinc-900 focus:outline-none"
              />
            </label>
          ))}
          <label className="text-sm">
            <span className="block text-zinc-500 font-medium mb-1">GPS mode</span>
            <select
              value={settings.priority}
              onChange={(e) => setSettings({ ...settings, priority: e.target.value })}
              className="w-full h-10 px-3 border-2 border-zinc-200 rounded-sm"
            >
              <option value="high_accuracy">High accuracy (GPS)</option>
              <option value="balanced">Battery saver (Wi-Fi / mobile network, less accurate)</option>
            </select>
          </label>
          <div className="md:col-span-3">
            <button disabled={saving} className="h-10 px-6 bg-zinc-900 text-white font-bold uppercase tracking-wider rounded-sm disabled:opacity-50">
              {saving ? 'Saving…' : 'Save settings'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

function AppReleasePanel() {
  const [open, setOpen] = useState(false);
  const [release, setRelease] = useState(null);
  const [form, setForm] = useState({ version_code: '', version_name: '', minimum_supported_version_code: '', release_notes: '' });
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(null);
  const downloadPage = `${window.location.origin}/app/download`;

  const loadRelease = useCallback(() => {
    axios.get(`${API}/app/version`).then((r) => setRelease(r.data)).catch(() => setRelease(null));
  }, []);

  useEffect(() => {
    if (open) loadRelease();
  }, [open, loadRelease]);

  const upload = async (e) => {
    e.preventDefault();
    if (!file) return;
    const body = new FormData();
    body.append('file', file);
    Object.entries(form).forEach(([k, v]) => body.append(k, v));
    setProgress(0);
    try {
      await axios.post(`${API}/app/releases`, body, {
        headers: authHeaders(),
        onUploadProgress: (p) => setProgress(p.total ? Math.round((p.loaded / p.total) * 100) : null),
      });
      toast.success('New app version published');
      setFile(null);
      loadRelease();
    } catch (error) {
      toast.error(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'Upload failed');
    } finally {
      setProgress(null);
    }
  };

  return (
    <div className="bg-white border-2 border-zinc-100 rounded-lg">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-2 p-4 font-bold uppercase tracking-wider text-sm text-zinc-900" data-testid="toggle-app-release">
        <Smartphone size={16} /> Android app (APK)
      </button>
      {open && (
        <div className="p-4 pt-0 space-y-4">
          <div className="text-sm space-y-1">
            {release?.available ? (
              <>
                <p>Current version: <b>{release.latest_version_name}</b> (code {release.latest_version_code}), minimum supported code {release.minimum_supported_version_code}</p>
                <p className="font-mono text-xs break-all text-zinc-600">SHA-256: {release.sha256}</p>
              </>
            ) : <p className="text-zinc-500">No APK published yet.</p>}
            <div className="flex items-center gap-2 pt-2">
              <span>Link to send employees on WhatsApp:</span>
              <code className="bg-zinc-100 px-2 py-1 rounded">{downloadPage}</code>
              <button type="button" onClick={() => navigator.clipboard?.writeText(downloadPage).then(() => toast.success('Link copied'))} className="text-zinc-600 hover:text-zinc-900">
                <Copy size={16} />
              </button>
            </div>
          </div>
          <form onSubmit={upload} className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t-2 border-zinc-100 pt-4">
            <label className="text-sm md:col-span-2">
              <span className="block text-zinc-500 font-medium mb-1">Signed release APK</span>
              <input type="file" accept=".apk,application/vnd.android.package-archive" required onChange={(e) => setFile(e.target.files[0])} />
            </label>
            {[
              ['version_code', 'Version code (number, from build.gradle.kts)', 'number'],
              ['version_name', 'Version name (e.g. 1.0.1)', 'text'],
              ['minimum_supported_version_code', 'Minimum supported version code (older apps cannot start duty)', 'number'],
            ].map(([key, label, type]) => (
              <label key={key} className="text-sm">
                <span className="block text-zinc-500 font-medium mb-1">{label}</span>
                <input type={type} required min={type === 'number' ? 1 : undefined} value={form[key]}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                  className="w-full h-10 px-3 border-2 border-zinc-200 rounded-sm focus:border-zinc-900 focus:outline-none" />
              </label>
            ))}
            <label className="text-sm md:col-span-2">
              <span className="block text-zinc-500 font-medium mb-1">Release notes (shown on the download page)</span>
              <textarea value={form.release_notes} maxLength={2000} onChange={(e) => setForm({ ...form, release_notes: e.target.value })}
                className="w-full h-20 p-3 border-2 border-zinc-200 rounded-sm focus:border-zinc-900 focus:outline-none" />
            </label>
            <div className="md:col-span-2">
              <button disabled={progress !== null} className="h-10 px-6 bg-zinc-900 text-white font-bold uppercase tracking-wider rounded-sm disabled:opacity-50">
                {progress !== null ? `Uploading… ${progress ?? ''}%` : 'Publish new version'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
